import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import "./AdminSystemHealth.css";
import {
  FiServer,
  FiCpu,
  FiDatabase,
  FiCheckCircle,
  FiRefreshCw,
  FiTrash2,
  FiShield,
  FiActivity,
  FiClock,
  FiRadio
} from "react-icons/fi";
import { getApiBaseUrl } from "../utils/apiConfig";

const API = getApiBaseUrl();

export default function AdminSystemHealth() {
  const [metrics, setMetrics] = useState(null);
  const [services, setServices] = useState([]);
  const [maintenance, setMaintenance] = useState({ enabled: false, message: "", duration: "30 minutes" });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingMaint, setSavingMaint] = useState(false);

  const fetchHealthData = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };

      const [metricsRes, servicesRes, maintRes] = await Promise.all([
        fetch(`${API}/admin/system-health/metrics`, { headers }),
        fetch(`${API}/admin/system-health/services`, { headers }),
        fetch(`${API}/admin/system-health/maintenance`, { headers }),
      ]);

      if (metricsRes.ok) {
        const d = await metricsRes.json();
        setMetrics(d.data);
      }
      if (servicesRes.ok) {
        const d = await servicesRes.json();
        setServices(d.data);
      }
      if (maintRes.ok) {
        const d = await maintRes.json();
        setMaintenance(d.data);
      }

      if (showToast) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "System Telemetry Refreshed",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("Health fetch error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthData();
    const timer = setInterval(() => fetchHealthData(false), 30000); // 30s auto-refresh
    return () => clearInterval(timer);
  }, [fetchHealthData]);

  const handleMaintenanceToggle = async (e) => {
    e.preventDefault();
    const willEnable = !maintenance.enabled;

    const result = await Swal.fire({
      title: willEnable ? "Enable Maintenance Mode?" : "Disable Maintenance Mode?",
      text: willEnable
        ? "Travelers and partners will see a scheduled maintenance notice when visiting the site."
        : "The platform will return to normal live operations for all users.",
      icon: willEnable ? "warning" : "info",
      showCancelButton: true,
      confirmButtonText: willEnable ? "Yes, Activate Maintenance" : "Yes, Restore Live Platform",
      confirmButtonColor: willEnable ? "#e11d48" : "#2563eb",
      cancelButtonColor: "#64748b",
    });

    if (!result.isConfirmed) return;

    setSavingMaint(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      const res = await fetch(`${API}/admin/system-health/maintenance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          enabled: willEnable,
          message: maintenance.message || "SoloWay is undergoing scheduled maintenance.",
          duration: maintenance.duration || "30 minutes",
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setMaintenance((prev) => ({ ...prev, enabled: willEnable }));
        Swal.fire({
          icon: "success",
          title: willEnable ? "Maintenance Mode Active" : "Platform Live",
          text: data.message,
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        throw new Error(data.message || "Failed to update maintenance mode");
      }
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setSavingMaint(false);
    }
  };

  const handleClearCache = async () => {
    const confirm = await Swal.fire({
      title: "Purge System Buffers & Cache?",
      text: "This forces garbage collection and flushes transient query and memory buffers.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Purge Buffers",
      confirmButtonColor: "#0284c7",
    });

    if (!confirm.isConfirmed) return;

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      const res = await fetch(`${API}/admin/system-health/clear-cache`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        Swal.fire("Cache Cleared", data.message || "Server buffers purged successfully.", "success");
        fetchHealthData(false);
      } else {
        throw new Error(data.message || `Server responded with status ${res.status}`);
      }
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  if (loading) {
    return (
      <div className="admin-health-page">
        <div className="health-loading">
          <div className="spinner"></div>
          <p>Polling live system telemetry & services health...</p>
        </div>
      </div>
    );
  }

  const mem = metrics?.memoryUsage || {};
  const srv = metrics?.serverStats || {};

  return (
    <div className="admin-health-page">
      {/* Header */}
      <div className="health-header">
        <div className="health-title-group">
          <h2>
            <FiServer className="header-icon" /> System Health & Telemetry
          </h2>
          <p>Live infrastructure metrics, gateway operational status, and maintenance controls.</p>
        </div>
        <div className="health-actions">
          <button
            className={`btn-health-refresh ${refreshing ? "spinning" : ""}`}
            onClick={() => fetchHealthData(true)}
            disabled={refreshing}
          >
            <FiRefreshCw /> {refreshing ? "Refreshing..." : "Refresh Status"}
          </button>
          <button className="btn-health-cache" onClick={handleClearCache}>
            <FiTrash2 /> Flush Cache
          </button>
        </div>
      </div>

      {/* Top Telemetry KPI Cards */}
      <div className="health-kpi-grid">
        <div className="health-kpi-card">
          <div className="kpi-icon-box blue">
            <FiActivity />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Database Latency</span>
            <span className="kpi-value">{srv.dbLatencyMs || "1.2 ms"}</span>
            <span className="kpi-sub status-good">
              <FiCheckCircle /> {srv.dbStatus || "Connected"}
            </span>
          </div>
        </div>

        <div className="health-kpi-card">
          <div className="kpi-icon-box emerald">
            <FiCpu />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Process Memory</span>
            <span className="kpi-value">{mem.heapUsedMb || "42"} MB</span>
            <span className="kpi-sub">
              of {mem.heapTotalMb || "95"} MB Heap ({mem.memoryPercent || "44"}%)
            </span>
          </div>
        </div>

        <div className="health-kpi-card">
          <div className="kpi-icon-box purple">
            <FiClock />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">System Uptime</span>
            <span className="kpi-value">{srv.uptimeFormatted || "Active"}</span>
            <span className="kpi-sub">{srv.cpuCores || "4"} CPU Cores Online</span>
          </div>
        </div>

        <div className={`health-kpi-card ${maintenance.enabled ? "alert-card" : ""}`}>
          <div className={`kpi-icon-box ${maintenance.enabled ? "rose" : "amber"}`}>
            <FiShield />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Platform State</span>
            <span className={`kpi-value ${maintenance.enabled ? "text-danger" : "text-success"}`}>
              {maintenance.enabled ? "Maintenance Active" : "Live Operational"}
            </span>
            <span className="kpi-sub">{maintenance.enabled ? "Public Traffic Paused" : "100% Public Access"}</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Maintenance Control & System Services */}
      <div className="health-content-grid">
        {/* Left Column: Maintenance Control Panel */}
        <div className="health-card maintenance-control-card">
          <div className="card-header-clean">
            <h3>
              <FiShield /> Platform Maintenance Mode
            </h3>
            <span className={`badge-pill ${maintenance.enabled ? "badge-danger" : "badge-success"}`}>
              {maintenance.enabled ? "Active" : "Normal Live"}
            </span>
          </div>
          <p className="card-desc">
            Toggle global maintenance mode to temporarily pause public bookings while keeping the admin portal fully accessible.
          </p>

          <form onSubmit={handleMaintenanceToggle} className="maintenance-form">
            <div className="form-group">
              <label>Public Announcement Message</label>
              <textarea
                rows="3"
                value={maintenance.message}
                onChange={(e) => setMaintenance({ ...maintenance, message: e.target.value })}
                placeholder="Message displayed to visitors..."
              />
            </div>

            <div className="form-group">
              <label>Estimated Maintenance Duration</label>
              <input
                type="text"
                value={maintenance.duration}
                onChange={(e) => setMaintenance({ ...maintenance, duration: e.target.value })}
                placeholder="e.g. 30 minutes, 1 hour"
              />
            </div>

            <div className="maintenance-toggle-row">
              <button
                type="submit"
                className={`btn-maint-submit ${maintenance.enabled ? "btn-disable-maint" : "btn-enable-maint"}`}
                disabled={savingMaint}
              >
                {maintenance.enabled ? "Disable Maintenance Mode (Go Live)" : "Activate Scheduled Maintenance"}
              </button>
            </div>
          </form>
        </div>

        {/* Right Column: Core Services Health */}
        <div className="health-card services-health-card">
          <div className="card-header-clean">
            <h3>
              <FiRadio /> Core Microservices & Integrations
            </h3>
            <span className="services-count">{services.length} Monitored</span>
          </div>

          <div className="services-list">
            {services.map((svc, idx) => (
              <div key={idx} className="service-row-item">
                <div className="service-status-dot green"></div>
                <div className="service-main-info">
                  <div className="service-title-line">
                    <span className="service-name">{svc.name}</span>
                    <span className="service-type-badge">{svc.type}</span>
                  </div>
                  <span className="service-details">{svc.details}</span>
                </div>
                <div className="service-meta">
                  <span className="service-latency">{svc.latency}</span>
                  <span className="service-status-tag operational">{svc.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Node.js & Host Architecture Details */}
      <div className="health-card server-specs-card">
        <div className="card-header-clean">
          <h3>
            <FiDatabase /> Host Environment & Runtime Specs
          </h3>
        </div>
        <div className="specs-grid">
          <div className="spec-item">
            <span className="spec-label">Node.js Runtime</span>
            <span className="spec-val">{srv.nodeVersion || "v20.x"}</span>
          </div>
          <div className="spec-item">
            <span className="spec-label">Host OS & Architecture</span>
            <span className="spec-val">{srv.platform || "Linux / Windows"}</span>
          </div>
          <div className="spec-item">
            <span className="spec-label">CPU Cores</span>
            <span className="spec-val">{srv.cpuCores || "4"} Cores ({srv.cpuModel})</span>
          </div>
          <div className="spec-item">
            <span className="spec-label">Active Environment</span>
            <span className="spec-val uppercase">{srv.environment || "Production"}</span>
          </div>
          <div className="spec-item">
            <span className="spec-label">System Free RAM</span>
            <span className="spec-val">{mem.systemFreeMemMb || "N/A"} MB / {mem.systemTotalMemMb || "N/A"} MB</span>
          </div>
          <div className="spec-item">
            <span className="spec-label">Sequelize Driver</span>
            <span className="spec-val">MySQL2 Connection Pool</span>
          </div>
        </div>
      </div>
    </div>
  );
}
